import React from 'react';
import { v4 as generateId } from 'uuid';
import clsx from 'clsx';

import { copyToClipboard } from '../../../utils/clipBoard';
import { Tooltip } from '../../../../../common/tooltip/Tooltip';
import {
  platformIds,
  COPY_CONFIRMATION_TIMEOUT,
  TOOLTIP_SUCCESS_TEXT,
  TOOLTIP_FAILED_TEXT,
} from '../../../constants';
import type { CopyButtonWrapperComponentProps } from '../../../types';
import shareButtonStyles from './ShareButton.module.scss';
import { HAS_CUSTOM_FOCUS_CLASSNAME } from '@wix/editor-elements-common-utils';
import { CopyButtonIcon } from './CopyButtonIcon';

const CopyButtonWrapper: React.FC<CopyButtonWrapperComponentProps> = ({
  id,
  shareButtonIcon,
  labelNode,
  url,
  ariaLabel,
  tooltipTexts,
}) => {
  const [tooltipText, setTooltipText] = React.useState('');
  const tooltipId = generateId();

  const _onClick = async () => {
    try {
      await copyToClipboard(decodeURI(url));
      setTooltipText(tooltipTexts?.linkCopiedText ?? TOOLTIP_SUCCESS_TEXT);
    } catch (error) {
      setTooltipText(tooltipTexts?.linkCopiedFailedText ?? TOOLTIP_FAILED_TEXT);
    }
  };

  React.useEffect(() => {
    if (!tooltipText) {
      return;
    }

    const timeout = setTimeout(() => {
      setTooltipText('');
    }, COPY_CONFIRMATION_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
  }, [tooltipText]);

  const classname = clsx(
    shareButtonStyles.root,
    shareButtonStyles.showTooltip,
    shareButtonStyles.container,
    'button',
  );

  return (
    <>
      {tooltipText && (
        <div className={classname} aria-hidden="true">
          <Tooltip
            tooltipId={tooltipId}
            dataTestId="copy-link-tooltip"
            iconComponent={() => <CopyButtonIcon labelNode={labelNode} />}
          >
            {tooltipText}
          </Tooltip>
        </div>
      )}
      <button
        id={id}
        className={clsx(
          classname,
          HAS_CUSTOM_FOCUS_CLASSNAME,
          tooltipText && shareButtonStyles.visuallyHidden,
        )}
        data-testid={platformIds.copyButton}
        onClick={_onClick}
        aria-label={ariaLabel}
      >
        {shareButtonIcon}
        {labelNode}
      </button>
      <div
        role="status"
        aria-live="polite"
        className={shareButtonStyles.visuallyHidden}
      >
        {tooltipText}
      </div>
    </>
  );
};

export default CopyButtonWrapper;
